import { API_BASE_URL } from '../config/constants';
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api';

/**
 * Service pour gérer les overlays de récompenses Twitch
 */
class OverlayService {
  /**
   * Liste tous les overlays
   * @returns {Promise<Array>} Liste des overlays
   */
  async getOverlays() {
    try {
      const response = await apiGet('/twitch/overlays');
      return response.data || [];
    } catch (error) {
      console.error('Erreur lors de la récupération des overlays:', error);
      throw error;
    }
  }

  /**
   * Récupère un overlay par rewardId
   * @param {string} rewardId - ID de la récompense
   * @returns {Promise<Object|null>} Overlay ou null
   */
  async getOverlayByRewardId(rewardId) {
    try {
      const response = await apiGet(`/twitch/overlays/${rewardId}`);
      return response.data || null;
    } catch (error) {
      if (error.status === 404) {
        return null;
      }
      console.error("Erreur lors de la récupération de l'overlay:", error);
      throw error;
    }
  }

  /**
   * Crée un overlay
   * @param {Object} overlayData - Données de l'overlay
   * @returns {Promise<Object>} Overlay créé
   */
  async createOverlay(overlayData) {
    try {
      const response = await apiPost('/twitch/overlays', overlayData);
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la création de l'overlay:", error);
      throw error;
    }
  }

  /**
   * Met à jour un overlay
   * @param {number} id - ID de l'overlay
   * @param {Object} overlayData - Nouvelles données
   * @returns {Promise<Object>} Overlay mis à jour
   */
  async updateOverlay(id, overlayData) {
    try {
      const response = await apiPut(`/twitch/overlays/${id}`, overlayData);
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la mise à jour de l'overlay:", error);
      throw error;
    }
  }

  /**
   * Supprime un overlay
   * @param {number} id - ID de l'overlay
   * @returns {Promise<void>}
   */
  async deleteOverlay(id) {
    try {
      await apiDelete(`/twitch/overlays/${id}`);
    } catch (error) {
      console.error("Erreur lors de la suppression de l'overlay:", error);
      throw error;
    }
  }

  /**
   * Déclenche un overlay de test (émission overlay.show côté serveur)
   * @param {number} id - ID de l'overlay
   * @returns {Promise<Object>} Données de test émises
   */
  async testOverlay(id) {
    try {
      const response = await apiPost(`/twitch/overlays/${id}/test`, {});
      return response.data;
    } catch (error) {
      console.error("Erreur lors du test de l'overlay:", error);
      throw error;
    }
  }

  /**
   * Déclenche l'affichage du classement (podium) sur l'overlay pour test
   * @returns {Promise<Object>} Données du test
   */
  async testLeaderboard() {
    try {
      const response = await apiPost('/twitch/overlays/test-leaderboard', {});
      return response;
    } catch (error) {
      console.error("Erreur lors du test du classement overlay:", error);
      throw error;
    }
  }

  /**
   * Déclenche un aperçu overlay sans sauvegarder (pour tester dans OBS sans récompense)
   * @param {Object} payload - { filePath, overlayType, duration? }
   * @returns {Promise<Object>} Données de l'aperçu émis
   */
  async testPreview(payload) {
    try {
      const response = await apiPost('/twitch/overlays/test-preview', payload);
      return response.data;
    } catch (error) {
      console.error("Erreur lors du test d'aperçu overlay:", error);
      throw error;
    }
  }

  /**
   * Démarre une battle viewers de test (HUD PV) dans l’overlay OBS.
   * @returns {Promise<Object>} state battle
   */
  async testBattleStart() {
    const response = await apiPost('/twitch/overlays/test-battle/start', {});
    return response.data;
  }

  /**
   * Simule une action sur la battle de test.
   * @param {{ action: 'attack'|'heal', team?: 'team1'|'team2' }} payload
   * @returns {Promise<Object>} state battle
   */
  async testBattleAction(payload) {
    const response = await apiPost('/twitch/overlays/test-battle/action', payload);
    return response.data;
  }

  /**
   * Lance une simulation automatique complète (faux combat avec adversaire).
   * Démarre la battle puis alterne les attaques des deux équipes jusqu'à fin.
   * @param {{ onTick?: Function, onEnd?: Function, intervalMs?: number }} opts
   * @returns {{ stop: Function }} Objet pour stopper la simulation manuellement
   */
  startBattleSimulation({ onTick, onEnd, intervalMs = 1000 } = {}) {
    let stopped = false;
    let turn = 0;

    const runSimulation = async () => {
      try {
        await this.testBattleStart();
        if (typeof onTick === 'function') onTick({ phase: 'started' });
      } catch (e) {
        if (typeof onEnd === 'function') onEnd({ error: e.message || 'Erreur démarrage battle' });
        return;
      }

      const tick = async () => {
        if (stopped) return;

        const team = turn % 2 === 0 ? 'team1' : 'team2';
        turn += 1;

        try {
          const state = await this.testBattleAction({ action: 'attack', team });
          if (typeof onTick === 'function') onTick({ phase: 'attack', team, state });

          const isOver = state && (state.team1Hp <= 0 || state.team2Hp <= 0 || state.active === false);
          if (isOver || stopped) {
            stopped = true;
            if (typeof onEnd === 'function') onEnd({ state });
            return;
          }

          setTimeout(tick, intervalMs);
        } catch (e) {
          stopped = true;
          if (typeof onEnd === 'function') onEnd({ error: e.message || 'Erreur simulation' });
        }
      };

      setTimeout(tick, intervalMs);
    };

    runSimulation();
    return { stop: () => { stopped = true; } };
  }

  /**
   * Upload un fichier overlay
   * @param {File} file - Fichier à uploader
   * @returns {Promise<Object>} Informations du fichier uploadé
   */
  async uploadOverlayFile(file) {
    const formData = new FormData();
    formData.append('overlay', file);

    const response = await fetch(`${API_BASE_URL}/uploads/overlay`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || "Erreur lors de l'upload du fichier");
    }

    const data = await response.json();
    return data.file;
  }

  /**
   * Récupère la taille totale du stockage utilisé
   * @returns {Promise<Object>} Statistiques de stockage
   */
  async getTotalStorageSize() {
    try {
      const response = await apiGet('/twitch/overlays/stats/storage');
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération du stockage:', error);
      throw error;
    }
  }

  /**
   * Construit l'URL complète d'un fichier overlay (vidéo, image).
   * En production (navigateur / OBS), retourne une URL absolue pour que la source
   * navigateur OBS charge correctement les médias.
   * @param {string} relativePath - Chemin relatif du fichier (ex: overlays/file.mp4)
   * @returns {string} URL complète (absolue en prod)
   */
  getFullUrl(relativePath) {
    if (!relativePath) return '';

    const trimmed = String(relativePath).trim();
    if (trimmed.includes('..') || trimmed.includes('\0')) {
      return '';
    }

    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      try {
        const parsed = new URL(trimmed);
        if (
          typeof window !== 'undefined' &&
          window.location?.origin &&
          parsed.origin === window.location.origin &&
          parsed.pathname.startsWith('/uploads/overlays/')
        ) {
          return trimmed;
        }
      } catch {
        return '';
      }
      return '';
    }

    /**
     * Normalisation des chemins d'assets côté backend.
     * En base : "overlays/..." → servi via "/uploads/overlays/...".
     */
    let normalizedRelativePath = relativePath.trim().replace(/\\/g, '/');

    if (
      normalizedRelativePath === 'overlays' ||
      normalizedRelativePath.startsWith('overlays/') ||
      normalizedRelativePath.startsWith('/overlays/')
    ) {
      normalizedRelativePath = normalizedRelativePath.startsWith('/')
        ? `/uploads${normalizedRelativePath}`
        : `/uploads/${normalizedRelativePath}`;
    }
    if (normalizedRelativePath.startsWith('uploads/')) {
      normalizedRelativePath = `/${normalizedRelativePath}`;
    }
    const normalizedPath = normalizedRelativePath.startsWith('/')
      ? normalizedRelativePath
      : `/${normalizedRelativePath}`;

    // En navigateur (ex: OBS Overlay Player), URL absolue pour chargement fiable
    if (typeof window !== 'undefined' && window.location?.origin) {
      return `${window.location.origin}${normalizedPath}`;
    }

    const baseUrl = API_BASE_URL.replace(/\/api\/?$/, '') || '';
    return baseUrl ? `${baseUrl}${normalizedPath}` : normalizedPath;
  }
}

const overlayService = new OverlayService();
export default overlayService;
